import React, { useState } from 'react';
import { useSelectedRestaurant } from '@/hooks/useSelectedRestaurant';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  getLocalLandings,
  getLandingStats,
  LocalLanding
} from '@/data/mocks/geo';
import { restaurants, getRestaurantById } from '@/data/mocks/restaurants';
import {
  Globe,
  MapPin,
  Languages,
  Building2,
  FileText,
  Image,
  ExternalLink,
  Eye,
  Copy,
  Star,
  MessageCircle,
  HelpCircle,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  ArrowRight
} from 'lucide-react';

export const GeoPage: React.FC = () => {
  const { selectedRestaurant, setSelectedRestaurant } = useSelectedRestaurant();
  const [selectedLanding, setSelectedLanding] = useState<LocalLanding | null>(null);

  const currentRestaurant = getRestaurantById(selectedRestaurant);
  const landings = getLocalLandings(selectedRestaurant);
  const stats = getLandingStats(selectedRestaurant);

  const previewLanding = (landing: LocalLanding) => {
    setSelectedLanding(landing);
  };

  const LandingPreview: React.FC<{ landing: LocalLanding }> = ({ landing }) => {
    return (
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="relative h-96 bg-gray-900 rounded-lg overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${landing.images.hero})`,
              backgroundBlendMode: 'overlay',
              backgroundColor: 'rgba(0,0,0,0.4)'
            }}
          />
          <div className="relative h-full flex flex-col justify-center items-center text-center text-white p-8">
            <h1 className="text-4xl font-bold mb-4">{landing.heroTitle}</h1>
            <p className="text-xl max-w-2xl">{landing.heroSubtitle}</p>
            <div className="flex gap-4 mt-8">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100">
                {landing.cta.primary.text}
              </Button>
              <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/20">
                {landing.cta.secondary.text}
              </Button>
            </div>
          </div>
        </div>

        {/* Location Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Location</h2>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">{landing.location.name}</h3>
                  <p className="text-gray-600">{landing.location.distance} from restaurant</p>
                  <div className="mt-2">
                    {landing.location.transportOptions.map((option, index) => (
                      <Badge key={index} variant="outline" className="mr-2 mb-2">
                        {option}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-4">Menu Highlights</h2>
              <div className="space-y-4">
                {landing.content.menu.items.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold">{item.name}</h4>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                      <Badge variant="outline">{item.price}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Reviews</h2>
              <div className="space-y-4">
                {landing.content.testimonials.map((testimonial, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span className="font-semibold">{testimonial.rating}/5</span>
                    </div>
                    <p className="text-gray-600 italic">"{testimonial.text}"</p>
                    <div className="mt-2 text-sm text-gray-500">
                      {testimonial.author} - {testimonial.source}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-4">FAQ</h2>
              <div className="space-y-4">
                {landing.content.faq.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-blue-500" />
                      {item.question}
                    </h4>
                    <p className="mt-2 text-gray-600">{item.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* En-tête avec sélecteur d'établissement */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">GEO - Generative Engine Optimization</h1>
          <p className="text-gray-600 mt-1">Optimisation pour les moteurs de recherche à IA générative</p>
        </div>
        <div className="flex gap-2">
          {Object.values(restaurants).map((restaurant) => (
            <Button
              key={restaurant.id}
              variant={selectedRestaurant === restaurant.id ? "default" : "outline"}
              onClick={() => setSelectedRestaurant(restaurant.id)}
              className="flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              {restaurant.name}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Section explicative GEO */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <Card className="border-2 bg-gradient-to-r from-amber-50 to-yellow-50" style={{ borderColor: 'rgba(233, 188, 102, 0.3)' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#E9BC66' }}>
                <Globe className="w-6 h-6 text-gray-900" />
              </div>
              Qu'est-ce que le GEO ?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-base leading-relaxed">
              Le <strong>GEO (Generative Engine Optimization)</strong> est une évolution du SEO classique qui vise à optimiser la visibilité et la recommandation des contenus dans les moteurs de recherche à IA générative (ChatGPT, Perplexity, Google Gemini, etc.).
            </p>
            <p className="text-base leading-relaxed">
              Il ne s'agit plus seulement de se positionner sur des mots-clés, mais <strong>d'apparaître dans les réponses produites par l'IA</strong>, en travaillant la pertinence sémantique, la fiabilité des sources et la popularité du contenu issue du trafic réel et des signaux de recommandation.
            </p>

            {/* Situation actuelle */}
            <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
              <h4 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Situation Actuelle - Le 39V
              </h4>
              <p className="text-sm text-red-800 mb-3">
                <strong>🚨 QUASI-INVISIBLE dans les recommandations IA</strong> : Le restaurant n'est quasiment jamais cité quand on cherche des recommandations de bons restaurants pour découvrir la gastronomie française depuis l'étranger.
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-sm text-red-700">
                  <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Test ChatGPT "best romantic restaurant in Paris" : Le 39V non mentionné</span>
                </div>
                <div className="flex items-start gap-2 text-sm text-red-700">
                  <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Test Perplexity "fine dining Paris with view" : 0 référence au restaurant</span>
                </div>
                <div className="flex items-start gap-2 text-sm text-red-700">
                  <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Google Gemini "French gastronomy experience Paris" : Absent des suggestions</span>
                </div>
              </div>
            </div>

            {/* Leviers d'action */}
            <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
              <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Leviers d'Optimisation GEO
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">FAQ Structurée Multilingue</p>
                    <p className="text-xs text-green-700">50+ questions/réponses en FR/EN/ES/PT avec données structurées</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">Backlinks Sources Premium</p>
                    <p className="text-xs text-green-700">Citations Michelin, Forbes, Condé Nast (sources fiables IA)</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">Contenu Exhaustif</p>
                    <p className="text-xs text-green-700">Guides "Ultimate" + entités nommées enrichies</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">Schema.org Restaurant</p>
                    <p className="text-xs text-green-700">Données structurées JSON-LD (menu, chef, avis)</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">Autorité Thématique</p>
                    <p className="text-xs text-green-700">Clusters "Fine Dining" + Knowledge Graph</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">Signaux Engagement</p>
                    <p className="text-xs text-green-700">Partages sociaux + UGC + mentions médias</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-4 pt-4 border-t border-green-300">
                <p className="text-sm text-green-800 mb-3">
                  <strong>Impact estimé :</strong> Être recommandé par les IA = 30-50% du trafic futur capté
                </p>
                <Button 
                  className="w-full"
                  style={{
                    backgroundColor: '#E9BC66',
                    color: '#1A1A1A'
                  }}
                >
                  Voir la Stratégie GEO Complète
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Statistiques */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Total Landings</p>
                <p className="text-2xl font-bold">{stats.totalLandings}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Globe className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Volume Ciblé</p>
                <p className="text-2xl font-bold">{stats.totalTargetedVolume.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Langues</p>
                <p className="text-2xl font-bold">
                  {Object.values(stats.byLanguage).filter(count => count > 0).length}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Languages className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Opportunité</p>
                <p className="text-2xl font-bold">{stats.averageOpportunity}%</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Star className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Liste des landings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Pages Locales</CardTitle>
            <CardDescription>
              {landings.length} landings disponibles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Landing</TableHead>
                    <TableHead>Langue</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Volume</TableHead>
                    <TableHead>KPI</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {landings.map((landing) => (
                    <TableRow key={landing.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{landing.title}</div>
                          <div className="text-sm text-gray-500">{landing.slug}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {landing.language.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {landing.location.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {landing.seoData.estimatedVolume.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          KD: {landing.seoData.competition}/100
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">Opportunité</span>
                            <Badge variant="success">
                              {landing.seoData.opportunity}%
                            </Badge>
                          </div>
                          <Progress
                            value={landing.seoData.opportunity}
                            className="h-2"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => previewLanding(landing)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Prévisualisation */}
      {selectedLanding && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Prévisualisation</CardTitle>
                  <CardDescription>{selectedLanding.title}</CardDescription>
                </div>
                <Button variant="outline" onClick={() => setSelectedLanding(null)}>
                  Fermer
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <LandingPreview landing={selectedLanding} />
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};